import React, {
  useEffect,
  useState,
} from "react";

import {
  useParams,
} from "react-router-dom";
import { toast } from "sonner";

import {
  publicSav,
} from "@/api";
import { formatApiError } from "@/lib/api";


const ISSUE_TYPES = [
  {
    value: "breakdown",
    label: "Panne",
  },
  {
    value: "blocked",
    label: "Machine bloquée",
  },
  {
    value: "quality",
    label: "Problème qualité",
  },
  {
    value: "noise",
    label: "Bruit anormal",
  },
  {
    value: "leak",
    label: "Fuite",
  },
  {
    value: "maintenance",
    label: "Maintenance / contrôle",
  },
  {
    value: "other",
    label: "Autre",
  },
];


const URGENCIES = [
  {
    value: "low",
    label: "Faible",
  },
  {
    value: "normal",
    label: "Normale",
  },
  {
    value: "high",
    label: "Haute",
  },
  {
    value: "urgent",
    label: "Urgente",
  },
];


const initialForm = {
  customer_name: "",
  company: "",
  phone: "",
  email: "",
  issue_type: "breakdown",
  urgency: "normal",
  description: "",
  consent_rgpd: false,
  attachments: [],
};


export default function PublicSav() {
  const { token } = useParams();

  const [machine, setMachine] =
    useState(null);
  const [form, setForm] =
    useState(initialForm);
  const [loading, setLoading] =
    useState(true);
  const [submitting, setSubmitting] =
    useState(false);
  const [submitted, setSubmitted] =
    useState(null);
  const [error, setError] =
    useState("");

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const result =
          await publicSav.getSavMachineInfo(
            token
          );

        if (!active) {
          return;
        }

        setMachine(
          result.machine || null
        );

        setForm((current) => ({
          ...current,
          company:
            result.machine?.company ||
            current.company,
        }));
      } catch (requestError) {
        if (!active) {
          return;
        }

        setError(
          formatApiError(
            requestError.response?.data?.detail
          ) ||
          "Lien SAV introuvable."
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      active = false;
    };
  }, [token]);

  const update = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const submit = async (event) => {
    event.preventDefault();

    if (!form.consent_rgpd) {
      toast.error(
        "Le consentement RGPD est obligatoire."
      );
      return;
    }

    setSubmitting(true);

    try {
      const result =
        await publicSav.submitSavTicket(
          token,
          {
            ...form,
            consent_rgpd:
              form.consent_rgpd
                ? "true"
                : "false",
          }
        );

      setSubmitted(result);
      toast.success(
        "Demande SAV envoyée."
      );
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError.response?.data?.detail
        ) ||
        "Impossible d'envoyer la demande SAV."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-[var(--do-bg-solid)] px-4 py-10">
        <div className="mx-auto max-w-2xl rounded-3xl bg-[var(--do-bg-solid)] p-8 shadow-sm">
          Chargement du formulaire SAV…
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="min-h-screen bg-[var(--do-bg-solid)] px-4 py-10">
        <div className="mx-auto max-w-2xl rounded-3xl bg-[var(--do-bg-solid)] p-8 shadow-sm">
          <h1 className="font-heading text-2xl font-black">
            Formulaire SAV indisponible
          </h1>

          <p className="mt-3 text-[var(--do-muted)]">
            {error}
          </p>
        </div>
      </main>
    );
  }

  if (submitted) {
    return (
      <main className="min-h-screen bg-[var(--do-bg-solid)] px-4 py-10">
        <div
          className="mx-auto max-w-2xl rounded-3xl bg-[var(--do-bg-solid)] p-8 shadow-sm"
          data-testid="public-sav-success"
        >
          <div className="text-sm font-bold uppercase tracking-[0.18em] text-[var(--do-accent)]">
            Demande transmise
          </div>

          <h1 className="mt-2 font-heading text-3xl font-black">
            Votre ticket SAV est ouvert
          </h1>

          <p className="mt-4 text-[var(--do-muted)]">
            Référence :{" "}
            <strong>
              {submitted.ticketNumber}
            </strong>
          </p>

          <p className="mt-2 text-[var(--do-muted)]">
            L'équipe concernée a été notifiée.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[var(--do-bg-solid)] px-4 py-10">
      <section className="mx-auto max-w-3xl rounded-3xl bg-[var(--do-bg-solid)] p-6 shadow-sm sm:p-8">
        <div className="text-sm font-bold uppercase tracking-[0.18em] text-[var(--do-accent)]">
          AlmaCRM · SAV
        </div>

        <h1 className="mt-2 font-heading text-3xl font-black text-[var(--do-ink)]">
          Déclarer une panne ou un problème
        </h1>

        <div className="mt-5 rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 text-sm text-[var(--do-ink)]">
          <div className="font-bold text-[var(--do-ink)]">
            {machine?.machineName ||
              "Machine"}
          </div>

          <div className="mt-1">
            N° série :{" "}
            {machine?.serialNumber || "—"}
          </div>

          <div>
            Site :{" "}
            {[
              machine?.siteName,
              machine?.city,
            ]
              .filter(Boolean)
              .join(" · ") || "—"}
          </div>
        </div>

        <form
          onSubmit={submit}
          className="mt-6 space-y-5"
          data-testid="public-sav-form"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-bold text-[var(--do-ink)]">
                Nom *
              </span>
              <input
                required
                value={form.customer_name}
                onChange={(event) =>
                  update(
                    "customer_name",
                    event.target.value
                  )
                }
                className="mt-1 w-full rounded-xl border border-[var(--do-border)] px-3 py-2"
              />
            </label>

            <label className="block">
              <span className="text-sm font-bold text-[var(--do-ink)]">
                Société
              </span>
              <input
                value={form.company}
                onChange={(event) =>
                  update(
                    "company",
                    event.target.value
                  )
                }
                className="mt-1 w-full rounded-xl border border-[var(--do-border)] px-3 py-2"
              />
            </label>

            <label className="block">
              <span className="text-sm font-bold text-[var(--do-ink)]">
                Téléphone *
              </span>
              <input
                required
                value={form.phone}
                onChange={(event) =>
                  update(
                    "phone",
                    event.target.value
                  )
                }
                className="mt-1 w-full rounded-xl border border-[var(--do-border)] px-3 py-2"
              />
            </label>

            <label className="block">
              <span className="text-sm font-bold text-[var(--do-ink)]">
                Email *
              </span>
              <input
                required
                type="email"
                value={form.email}
                onChange={(event) =>
                  update(
                    "email",
                    event.target.value
                  )
                }
                className="mt-1 w-full rounded-xl border border-[var(--do-border)] px-3 py-2"
              />
            </label>

            <label className="block">
              <span className="text-sm font-bold text-[var(--do-ink)]">
                Type de panne *
              </span>
              <select
                value={form.issue_type}
                onChange={(event) =>
                  update(
                    "issue_type",
                    event.target.value
                  )
                }
                className="mt-1 w-full rounded-xl border border-[var(--do-border)] px-3 py-2"
              >
                {ISSUE_TYPES.map((item) => (
                  <option
                    key={item.value}
                    value={item.value}
                  >
                    {item.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-sm font-bold text-[var(--do-ink)]">
                Urgence *
              </span>
              <select
                value={form.urgency}
                onChange={(event) =>
                  update(
                    "urgency",
                    event.target.value
                  )
                }
                className="mt-1 w-full rounded-xl border border-[var(--do-border)] px-3 py-2"
              >
                {URGENCIES.map((item) => (
                  <option
                    key={item.value}
                    value={item.value}
                  >
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-bold text-[var(--do-ink)]">
              Description *
            </span>
            <textarea
              required
              rows={6}
              value={form.description}
              onChange={(event) =>
                update(
                  "description",
                  event.target.value
                )
              }
              className="mt-1 w-full rounded-xl border border-[var(--do-border)] px-3 py-2"
              placeholder="Décrivez le problème, le message d'erreur, le contexte, les symptômes observés…"
            />
          </label>

          <label className="block">
            <span className="text-sm font-bold text-[var(--do-ink)]">
              Photos / pièces jointes
            </span>
            <input
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.webp,.pdf"
              onChange={(event) =>
                update(
                  "attachments",
                  event.target.files
                )
              }
              className="mt-1 w-full rounded-xl border border-[var(--do-border)] px-3 py-2"
            />
            <span className="mt-1 block text-xs text-[var(--do-muted)]">
              Formats acceptés : JPG, PNG, WEBP, PDF. Maximum 10 Mo par fichier.
            </span>
          </label>

          <label className="flex gap-3 rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 text-sm text-[var(--do-ink)]">
            <input
              type="checkbox"
              checked={form.consent_rgpd}
              onChange={(event) =>
                update(
                  "consent_rgpd",
                  event.target.checked
                )
              }
              className="mt-1"
            />

            <span>
              J'accepte que ces informations soient utilisées pour traiter ma demande SAV.
            </span>
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-2xl bg-[var(--do-accent-strong)] px-5 py-3 font-heading font-black text-[var(--do-on-accent)] transition hover:bg-[var(--do-accent-strong)] disabled:opacity-60"
          >
            {submitting
              ? "Envoi en cours…"
              : "Envoyer la demande SAV"}
          </button>
        </form>
      </section>
    </main>
  );
}
